/**
 * One funny name, rolled.
 *
 * There is no text field anywhere in this game: a player is given a name and one dice button that
 * gives them another. That is a deliberate trade. A name typed by a stranger is a moderation
 * problem on everybody else's screen, and a name rolled from vocabulary this build owns is not one.
 *
 * The picker is injected, which is what makes this testable: the default reads `crypto`, the same
 * source `mintRejoinToken` uses, and a test hands in a picker that always takes the first item.
 * `Math.random` is not used here even though this is not simulation code, because the repo has
 * exactly one answer to where randomness comes from and two would be one too many.
 */

import { NAME_PATTERNS } from '@/identity/namePatternRegistry';
import type { WordPicker } from '@/identity/playerNameContract';

/**
 * Uniform over the list, by rejection rather than by remainder.
 *
 * A plain modulo of a 32-bit word favours the first few entries of any list whose length is not a
 * power of two. Nobody would ever notice it on a name, but a biased picker is the kind of thing
 * that gets copied somewhere it matters.
 */
export const cryptoPick: WordPicker = <T>(items: readonly T[]): T => {
  // Checked before the draw rather than after it: an empty list gives the rejection loop below no
  // value it can ever accept, so reaching it first would spin rather than throw.
  if (items.length === 0) throw new RangeError('cryptoPick: cannot pick from an empty list');

  const chosen = items[uniformIndex(items.length)];
  if (chosen === undefined) {
    throw new RangeError('cryptoPick: drew an index the list does not have');
  }
  return chosen;
};

export function rollPlayerName(pick: WordPicker = cryptoPick): string {
  return pick(NAME_PATTERNS).build(pick);
}

function uniformIndex(length: number): number {
  const limit = Math.floor(0x100000000 / length) * length;
  const word = new Uint32Array(1);

  for (;;) {
    crypto.getRandomValues(word);
    const drawn = word[0] ?? 0;
    if (drawn < limit) return drawn % length;
  }
}
